import { useEffect, useState } from "react";
import {
  Building2,
  Calendar,
  Clock3,
  DollarSign,
  FolderKanban,
  Tag,
  FileText,
  CheckCircle2,
  CircleDot,
  ListTodo,
} from "lucide-react";

import {
  getWorkspaces,
  getWorkspaceItems,
} from "../services/workspaceService";

const StatCard = ({ title, value, icon: Icon, iconClass }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`rounded-lg p-3 ${iconClass}`}>
        <Icon size={22} />
      </div>

      <div>
        <p className="text-sm text-slate-500">{title}</p>

        <h3 className="text-2xl font-bold text-slate-800">
          {value}
        </h3>
      </div>
    </div>
  </div>
);

const InfoItem = ({ icon: Icon, title, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 rounded-lg bg-slate-100 p-2">
      <Icon size={17} className="text-slate-500" />
    </div>

    <div>
      <p className="text-xs text-slate-500">
        {title}
      </p>

      <p className="mt-1 text-sm font-semibold text-slate-800">
        {value || "Not specified"}
      </p>
    </div>
  </div>
);

const ProjectInformation = ({
  project,
  employees = [],
  employeesLoading = false,
}) => {
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    activeTasks: 0,
    workspaces: 0,
    progress: 0,
  });

  const [loading, setLoading] = useState(false);

  // ============================================================
  // PROJECT UUID
  // ============================================================

  const projectUuid =
    project?.uuid ||
    project?.project_uuid ||
    project?.projectUuid;

  // ============================================================
  // RESPONSE ARRAY HELPER
  // ============================================================

  const getArray = (response) => {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.data?.data)) {
      return response.data.data;
    }

    return [];
  };

  // ============================================================
  // LOAD TASKS FROM ALL WORKSPACES
  // ============================================================

  const loadProjectOverview = async () => {
    if (!projectUuid) return;

    try {
      setLoading(true);

      // Get all workspaces of this project
      const workspaceResponse =
        await getWorkspaces(projectUuid);

      const workspaces =
        getArray(workspaceResponse);

      let totalTasks = 0;
      let completedTasks = 0;

      // Get tasks from every workspace
      const workspaceResults = await Promise.all(
        workspaces.map(async (workspace) => {
          const workspaceUuid =
            workspace?.uuid ||
            workspace?.workspace_uuid ||
            workspace?.workspaceUuid ||
            workspace?.id;

          if (!workspaceUuid) {
            return [];
          }

          try {
            const response =
              await getWorkspaceItems(workspaceUuid);

            return getArray(response);
          } catch (error) {
            console.error(
              "Failed to get workspace tasks:",
              workspaceUuid,
              error
            );

            return [];
          }
        })
      );

      // Calculate total and completed tasks
      workspaceResults.forEach((items) => {
        totalTasks += items.length;

        completedTasks += items.filter((item) => {
          const status = String(
            item?.status || ""
          )
            .toLowerCase()
            .trim();

          return (
            status === "completed" ||
            status === "complete" ||
            status === "done"
          );
        }).length;
      });

      const activeTasks =
        totalTasks - completedTasks;

      const progress =
        totalTasks > 0
          ? Math.round(
              (completedTasks / totalTasks) * 100
            )
          : 0;

      const finalStats = {
        totalTasks,
        completedTasks,
        activeTasks,
        workspaces: workspaces.length,
        progress,
      };

      console.log(
        "========== PROJECT OVERVIEW =========="
      );

      console.log(
        "Project UUID:",
        projectUuid
      );

      console.log(
        "Workspaces:",
        workspaces.length
      );

      console.log(
        "Total Tasks:",
        totalTasks
      );

      console.log(
        "Completed Tasks:",
        completedTasks
      );

      console.log(
        "Active Tasks:",
        activeTasks
      );

      console.log(
        "Progress:",
        `${progress}%`
      );

      setStats(finalStats);
    } catch (error) {
      console.error(
        "PROJECT OVERVIEW ERROR:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // LOAD WHEN PROJECT CHANGES
  // ============================================================

  useEffect(() => {
    if (!projectUuid) return;

    loadProjectOverview();
  }, [projectUuid]);

  // ============================================================
  // PROJECT MANAGER
  // ============================================================

  const managerUuid =
    project?.project_manager_employee_uuid ||
    project?.project_manager_uuid ||
    project?.project_manager?.uuid ||
    "";

  const projectManagerEmployee =
    employees.find((employee) => {
      const employeeUuid =
        employee?.uuid ||
        employee?.employee_uuid ||
        employee?.id;

      return (
        String(employeeUuid) ===
        String(managerUuid)
      );
    });

  const projectManagerName =
    project?.project_manager_name ||
    project?.project_manager?.name ||
    projectManagerEmployee?.name ||
    projectManagerEmployee?.employee_name ||
    projectManagerEmployee?.full_name ||
    projectManagerEmployee?.employeeName ||
    (
      projectManagerEmployee?.first_name &&
      projectManagerEmployee?.last_name
        ? `${projectManagerEmployee.first_name} ${projectManagerEmployee.last_name}`
        : ""
    ) ||
    (
      projectManagerEmployee?.firstName &&
      projectManagerEmployee?.lastName
        ? `${projectManagerEmployee.firstName} ${projectManagerEmployee.lastName}`
        : ""
    ) ||
    (
      employeesLoading
        ? "Loading..."
        : "Not assigned"
    );

  // ============================================================
  // DATE FORMAT
  // ============================================================

  const formatDate = (value) => {
    if (!value) {
      return "Not specified";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Not specified";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ============================================================
  // CURRENCY FORMAT
  // ============================================================

  const formatCurrency = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "Not specified";
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(value));
  };

  // ============================================================
  // PROJECT DATA
  // ============================================================

  if (!project) {
    return null;
  }

  const estimatedHours =
    project.estimated_hours;

  const estimatedCost =
    project.estimated_cost;

  const tags =
    Array.isArray(project.tags)
      ? project.tags
      : [];

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="space-y-5">

      {/* ======================================================
          TASK STATISTICS
      ====================================================== */}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

        <StatCard
          title="Total Tasks"
          value={
            loading
              ? "..."
              : stats.totalTasks
          }
          icon={ListTodo}
          iconClass="bg-blue-100 text-blue-600"
        />

        <StatCard
          title="Completed Tasks"
          value={
            loading
              ? "..."
              : stats.completedTasks
          }
          icon={CheckCircle2}
          iconClass="bg-green-100 text-green-600"
        />

        <StatCard
          title="Active Tasks"
          value={
            loading
              ? "..."
              : stats.activeTasks
          }
          icon={CircleDot}
          iconClass="bg-yellow-100 text-yellow-600"
        />

        <StatCard
          title="Workspaces"
          value={
            loading
              ? "..."
              : stats.workspaces
          }
          icon={FolderKanban}
          iconClass="bg-purple-100 text-purple-600"
        />

      </div>

      {/* ======================================================
          OVERALL TASK PROGRESS
      ====================================================== */}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">

        <div className="mb-2 flex items-center justify-between">

          <span className="text-sm font-semibold text-slate-700">
            Overall Task Progress
          </span>

          <span className="text-sm font-bold text-blue-600">
            {loading
              ? "..."
              : `${stats.progress}%`}
          </span>

        </div>

        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">

          <div
            className="h-2 rounded-full bg-blue-600 transition-all duration-500"
            style={{
              width: `${stats.progress}%`,
            }}
          />

        </div>

      </div>

      {/* ======================================================
          DESCRIPTION
      ====================================================== */}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

        <div className="flex items-center gap-3">

          <div className="rounded-lg bg-blue-50 p-2">
            <FileText
              size={19}
              className="text-blue-600"
            />
          </div>

          <h3 className="text-lg font-semibold text-slate-800">
            Project Description
          </h3>

        </div>

        <p className="mt-5 leading-7 text-slate-600">
          {project.description ||
            "No description provided."}
        </p>

      </div>

      {/* ======================================================
          PROJECT INFORMATION
      ====================================================== */}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

        <h3 className="mb-6 text-lg font-semibold text-slate-800">
          Project Information
        </h3>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

          <InfoItem
            icon={Building2}
            title="Client"
            value={
              project.client_name ||
              "Internal"
            }
          />

          <InfoItem
            icon={FolderKanban}
            title="Project Type"
            value={
              project.project_type
            }
          />

          <InfoItem
            icon={Calendar}
            title="Start Date"
            value={formatDate(
              project.planned_start_date ||
              project.startDate
            )}
          />

          <InfoItem
            icon={Calendar}
            title="End Date"
            value={formatDate(
              project.planned_end_date ||
              project.endDate
            )}
          />

          <InfoItem
            icon={Clock3}
            title="Estimated Hours"
            value={
              estimatedHours !== null &&
              estimatedHours !== undefined
                ? `${estimatedHours} hrs`
                : "Not specified"
            }
          />

          <InfoItem
            icon={DollarSign}
            title="Estimated Cost"
            value={formatCurrency(
              estimatedCost
            )}
          />

          

        </div>

      </div>

      {/* ======================================================
          PROJECT CONFIGURATION
      ====================================================== */}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

        <h3 className="mb-6 text-lg font-semibold text-slate-800">
          Project Configuration
        </h3>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

          <InfoItem
            icon={FolderKanban}
            title="Execution Mode"
            value={
              project.execution_mode
            }
          />

          <InfoItem
            icon={Tag}
            title="Priority"
            value={
              project.priority
            }
          />

          <InfoItem
            icon={FolderKanban}
            title="Status"
            value={
              project.status
            }
          />

          <InfoItem
            icon={FolderKanban}
            title="Project Manager"
            value={projectManagerName}
          />

        </div>

      </div>

      {/* ======================================================
          TAGS
      ====================================================== */}

      {tags.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">

          <div className="mb-4 flex items-center gap-2">

            <Tag
              size={18}
              className="text-blue-600"
            />

            <h3 className="text-lg font-semibold text-slate-800">
              Tags
            </h3>

          </div>

          <div className="flex flex-wrap gap-2">

            {tags.map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700"
              >
                {tag}
              </span>
            ))}

          </div>

        </div>
      )}

    </div>
  );
};

export default ProjectInformation;